import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Chip,
  Paper,
  Button,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTenantConfig } from '../hooks/useTenant';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LockIcon from '@mui/icons-material/Lock';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DrawIcon from '@mui/icons-material/Draw';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SmsIcon from '@mui/icons-material/Sms';

const P = {
  main: '#0891b2',
  light: '#06b6d4',
  dark: '#0e7490',
};


  const mainFeatures = [
    {
      image: '/assets/icon/queue.webp',
      title: 'Üye Kayıt & Profil Yönetimi',
      desc: 'Üyelerin temel bilgilerini ve iletişim detaylarını düzenli şekilde yönetin.',
      color: '#f97316'
    },
    {
      image: '/assets/icon/user.webp',
      title: 'Üyelik Durumu Takibi',
      desc: 'Aktif, pasif veya ayrılan üyeleri net biçimde ayırın.',
      color: '#0891b2'
    },
    {
      image: '/assets/icon/salary.webp',
      title: ' Aidat & Borç Bilgileri',
      desc: 'Üyeye özel aidat dönemleri ve ödeme geçmişini görüntüleyin.',
      color: '#f43f5e'
    },
    {
      image: '/assets/icon/project-manager.webp',
      title: 'Yetki & Rol Yönetimi',
      desc: 'Yönetici ve personel erişimlerini kontrollü şekilde belirleyin.',
      color: '#8b5cf6'
    },
    {
      image: '/assets/icon/experience.webp',
      title: 'Üye Geçmişi',
      desc: ' Üyenin tüm işlemleri tek profil altında kayıtlı olsun.',
      color: '#10b981'
    },
    {
      image: '/assets/icon/efficiency.webp',
      title: 'Toplu İşlemler',
      desc: ' Üyeleri toplu ekleyin, güncelleyin veya bildirim gönderin.',
      color: '#eab308'
    },
  ];

export default function UyeYonetimiPage() {
  const navigate = useNavigate();
  const { tenantName } = useTenantConfig();
  return (
    <>
      {/* ================= HERO ================= */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${alpha(P.main, 0.06)} 0%, #ffffff 70%)`,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(P.main, 0.08)} 0%, transparent 70%)`,
            filter: 'blur(80px)',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720, position: 'relative', zIndex: 1 }}>
            <Chip
              label="Üye & Yönetim Modülü"
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.8rem',
                background: alpha(P.main, 0.1),
                color: P.dark,
                border: `1px solid ${alpha(P.main, 0.2)}`,
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.1,
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Üyelerinizi Sadece Kaydetmeyin,{' '}
              <Box component="span" sx={{
                background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Derneğinizin Merkezine Alın
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#64748b',
                lineHeight: 1.7,
                mb: 5,
              }}
            >
              Üye bilgileri, aidat durumu, katılım geçmişi ve iletişim süreçlerini tek yerden yönetin.
              Her üye ile düzenli, kontrollü ve sürdürülebilir bir bağ kurun.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                //onClick={() => navigate('/basvuru')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: `linear-gradient(135deg, ${P.main} 0%, ${P.dark} 100%)`,
                  boxShadow: `0 8px 24px ${alpha(P.main, 0.35)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${P.dark} 0%, #155e75 100%)`,
                    boxShadow: `0 12px 32px ${alpha(P.main, 0.45)}`,
                  },
                }}
              >
                Hemen Başlayın
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 1️⃣ PROBLEM */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  color: '#0f172a',
                  fontSize: { xs: '1.9rem', md: '2.4rem' }
                }}
              >
                Üyelerinizi Düzenli, Şeffaf ve
                <br /> Kontrol Altında Yönetin
              </Typography>

              <Typography
                sx={{
                  fontSize: '1.05rem',
                  color: '#475569',
                  lineHeight: 1.8,
                  mb: 4,
                  maxWidth: 520
                }}
              >
                Üye kayıtlarından durum takibine, aidat ve iletişim
                süreçlerine kadar derneğinizin tüm üye yapısını
                tek sistem üzerinden yönetin.
              </Typography>

              <Stack spacing={2}>
                <Feature text="Üye kayıt ve profil yönetimi" />
                <Feature text="Üyelik durumu ve geçmiş takibi" />
                <Feature text="Aidat ve borç bilgileri" />
                <Feature text="Yetki bazlı erişim kontrolü" />
              </Stack>
            </Grid>

            {/* Sağ: Görsel / Mock */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  height: { xs: 300, md: 470 },
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: '1px solid #e5e7eb',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                  mx: 'auto',
                }}
              >
                <Box
                  component={'img'}
                  src='/assets/page_images/uye-yonetimi.webp'
                  alt="Üye Yönetimi Ekranı"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 2️⃣ SÜREÇ */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ color: '#0f172a', fontSize: { xs: '1.75rem', md: '2.25rem' } }}
            >
              Dernekte Üye Yönetimi Nasıl İşler?
            </Typography>
            <Typography color="#64748b" maxWidth={600} mx="auto" fontSize="1rem">
              Üyeler yalnızca kayıt olarak değil; aidat, etkinlik ve
              finansal süreçlerle birlikte yönetilir.
            </Typography>
          </Box>

          {/* Timeline Style Steps */}
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Grid container spacing={3}>
              {[
                { number: '1', text: 'Üye kaydı oluşturulur', desc: 'Temel bilgiler sisteme alınır' },
                { number: '2', text: 'Üyelik türü ve durumu belirlenir', desc: 'Aktif, pasif veya onay bekliyor' },
                { number: '3', text: 'Aidat ve finansal ilişkiler bağlanır', desc: 'Borç ve ödeme takibi başlar' },
                { number: '4', text: 'Tüm geçmiş kayıt altına alınır', desc: 'İşlemler loglanır ve raporlanır' },
              ].map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box
                    sx={{
                      position: 'relative',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      bgcolor: '#fafafa',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: P.main,
                        bgcolor: 'white',
                        boxShadow: `0 8px 24px ${alpha(P.main, 0.12)}`,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    {/* Number badge */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        border: `2px solid ${P.main}`,
                        bgcolor: 'white',
                        color: P.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '1rem',
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {step.number}
                    </Box>
                    
                    <Typography 
                      fontWeight={700} 
                      color="#0f172a" 
                      fontSize="0.95rem"
                      mb={0.5}
                    >
                      {step.text}
                    </Typography>
                    <Typography 
                      color="#94a3b8" 
                      fontSize="0.8rem"
                      lineHeight={1.5}
                    >
                      {step.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* 2️⃣.5 AİDAT & ÜYE İLİŞKİSİ */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="lg">
          {/* Başlık */}
          <Box textAlign="center" mb={8}>
            <Chip
              label="AİDAT & ÜYE İLİŞKİSİ"
              sx={{
                mb: 2,
                fontWeight: 700,
                backgroundColor: alpha(P.main, 0.12),
                color: P.main,
                border: 'none'
              }}
            />

            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ color: '#0f172a', fontSize: { xs: '1.75rem', md: '2.25rem' } }}
            >
              Aidat Süreci Üye ile Nasıl Bağlanır?
            </Typography>

            <Typography
              color="#64748b"
              maxWidth={720}
              mx="auto"
              fontSize="1.05rem"
            >
              Aidat işlemleri sadece tahsilat değildir. Üye durumu, borç bilgisi,
              muhasebe ve raporlama süreçleri tek bir akış içinde çalışır.
            </Typography>
          </Box>

          {/* Akış Kartları */}
          <Grid container spacing={3}>
            {[
              { title: 'Üye Kaydı Oluşturulur', desc: 'Üyelik bilgileri sisteme alınır.' },
              { title: 'Aidat Tanımlanır', desc: 'Dönem, tutar ve ödeme tipi belirlenir.' },
              { title: 'Borçlandırma Yapılır', desc: 'Üye hesabına otomatik borç yansır.' },
              { title: 'Tahsilat Alınır', desc: 'Online veya manuel ödeme kaydedilir.' },
              { title: 'Muhasebe Kaydı Oluşur', desc: 'Gelir otomatik muhasebeye işlenir.' },
              { title: 'Üye Durumu Güncellenir', desc: 'Borçlu / aktif durum anlık güncellenir.' },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: alpha(P.main, 0.3),
                      boxShadow: `0 8px 24px ${alpha(P.main, 0.12)}`
                    }
                  }}
                >
                  <Typography fontWeight={700} color="#0f172a" mb={1} fontSize="1.05rem">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="#64748b" lineHeight={1.6}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Alt vurgu */}
          <Box sx={{
            display:'flex',
            alignItems:'center',
            justifyContent:'center',
            width:'100%'
          }}>
            <Box
            sx={{
              mt: 8,
              p: 1,
              textAlign: 'center',
              borderRadius: 3,
              backgroundColor: alpha(P.main, 0.08),
              border: `1px solid ${alpha(P.main, 0.2)}`,
              width: { xs: '100%', md: '70%' },
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                color: P.dark,
                fontSize: '1.05rem'
              }}
            >
              Aidat süreci tamamlandıkça üye bilgileri otomatik güncellenir
            </Typography>
          </Box>
          </Box>
        </Container>
      </Box>

      {/* 3️⃣ ANA ÖZELLİKLER */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ color: '#0f172a', fontSize: { xs: '1.75rem', md: '2.5rem' }, mb: 2 }}
            >
              Üye Yönetimi Özellikleri
            </Typography>
            <Typography
              color="#64748b"
              fontSize="1.1rem"
              sx={{ maxWidth: '600px', mx: 'auto' }}
            >
              Dernek ihtiyaçlarına göre tasarlanmış güçlü yapı
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {mainFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      borderColor: feature.color
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box
                      sx={{
                        padding: 1.5,
                        width: 75,
                        height: 75,
                        borderRadius: '12px',
                        bgcolor: alpha(feature.color, 0.1),
                        color: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <img src={feature.image} alt={feature.title} style={{ width: '100%', height: '100%' }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 1.5, fontSize: '1rem' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6, fontSize: '0.9rem' }}
                    >
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4️⃣ ONLINE BAŞVURU SİSTEMİ - GİRİŞ */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff', borderTop: '1px solid #f1f5f9' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="ONLINE ÜYELİK BAŞVURU SİSTEMİ"
                icon={<GroupAddIcon />}
                sx={{ mb: 3, fontWeight: 700, fontSize: '0.78rem', background: alpha(P.main, 0.1), color: P.dark, border: `1px solid ${alpha(P.main, 0.2)}` }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, color: '#0f172a', fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
                Online Üyelik Sistemi ile{' '}
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Başvuruları Kolayca Yönetin
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.8, mb: 3 }}>
                {tenantName} Online Üyelik Başvuru Sistemi, dernek üyelik süreçlerinizi tamamen dijital ortama taşıyarak başvuruların hızlı, düzenli ve sorunsuz şekilde alınmasını sağlar.
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.8 }}>
                Web sitenize kolayca entegre edilen başvuru formu sayesinde, aday üyeler başvurularını birkaç adımda online olarak tamamlayabilir. Böylece tüm başvurular tek bir sistem üzerinden merkezi olarak yönetilir.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { icon: CheckCircleOutlineIcon, color: '#10b981', title: 'Başvuru kaydı alınır', desc: 'Form doldurulur, sistem otomatik kayıt oluşturur.' },
                  { icon: CheckCircleOutlineIcon, color: P.main, title: 'Gerekli kontroller yapılır', desc: 'Bilgiler doğrulanır, eksikler tespit edilir.' },
                  { icon: CheckCircleOutlineIcon, color: '#8b5cf6', title: 'Yönetici onayı ile tamamlanır', desc: 'Onaylandıktan sonra üyelik süreci başlar.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fafafa' }}>
                      <Icon sx={{ color: item.color, mt: 0.3, flexShrink: 0 }} />
                      <Box>
                        <Typography fontWeight={700} color="#0f172a" fontSize="0.95rem">{item.title}</Typography>
                        <Typography variant="body2" color="#64748b">{item.desc}</Typography>
                      </Box>
                    </Box>
                  );
                })}
                <Box sx={{ mt: 1, p: 2.5, borderRadius: 3, bgcolor: alpha(P.main, 0.07), border: `1px solid ${alpha(P.main, 0.15)}` }}>
                  <Typography fontWeight={700} color={P.dark} mb={1}>Belgeler otomatik hazırlanır:</Typography>
                  <Stack spacing={0.5}>
                    {['Derneğe özel üyelik formu', 'Başvuru çıktıları', 'PDF indirme & SMS/e-posta gönderimi'].map((t, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: P.main, flexShrink: 0 }} />
                        <Typography variant="body2" color="#334155">{t}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 4️⃣b FORM ÖZELLEŞTİRME */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={7}>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#0f172a', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 2 }}>
              Başvuru Formunu İstediğiniz Gibi Oluşturun
            </Typography>
            <Typography color="#64748b" maxWidth={640} mx="auto" fontSize="1.05rem">
              Sadece temel bilgiler değil, derneğinizin yapısına göre istediğiniz her veriyi toplayabilirsiniz.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              { emoji: '🧑‍💼', title: 'İş İnsanları Dernekleri', fields: ['Firma adı', 'Sektör', 'Görev / Ünvan', 'Ticari bilgiler'] },
              { emoji: '🐾', title: 'Hayvan Koruma Dernekleri', fields: ['Gönüllülük deneyimi', 'Barınak çalışmasına uygunluk', 'Sahiplendirme ilgisi'] },
              { emoji: '🎓', title: 'Eğitim / Öğrenci Dernekleri', fields: ['Okul / bölüm', 'Eğitim durumu', 'İlgi alanları'] },
              { emoji: '🚗', title: 'Meslek / Hobi Dernekleri', fields: ['Ehliyet bilgisi', 'Sertifikalar', 'Deneyim süresi'] },
              { emoji: '👨‍👩‍👧‍👦', title: 'Sosyal Yardım Dernekleri', fields: ['Medeni durum', 'Gönüllülük tercihleri', 'Katılım sıklığı'] },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card elevation={0} sx={{ height: '100%', border: '1px solid #e2e8f0', borderRadius: 3, transition: 'all 0.2s', '&:hover': { borderColor: P.main, transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${alpha(P.main, 0.12)}` } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography fontSize="2rem" mb={1}>{item.emoji}</Typography>
                    <Typography fontWeight={700} color="#0f172a" fontSize="1rem" mb={1.5}>{item.title}</Typography>
                    <Stack spacing={0.7}>
                      {item.fields.map((f, fi) => (
                        <Stack key={fi} direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: P.main, flexShrink: 0 }} />
                          <Typography variant="body2" color="#64748b">{f}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={0} sx={{ height: '100%', border: `2px dashed ${alpha(P.main, 0.3)}`, borderRadius: 3, bgcolor: alpha(P.main, 0.03) }}>
                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color={P.main} fontWeight={700} textAlign="center" fontSize="1rem">
                    Her dernek kendi yapısına özel tamamen özelleştirilmiş bir başvuru deneyimi sunabilir.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 4️⃣c GELİŞMİŞ FORM ÖZELLİKLERİ */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={7}>
            <Typography variant="h4" fontWeight={800} sx={{ color: '#0f172a', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 2 }}>
              Gelişmiş Form Özellikleri
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              { Icon: AttachFileIcon, color: '#f97316', title: 'Dosya Yükleme', desc: 'Kimlik, fotoğraf veya ek belgeleri zorunlu ya da isteğe bağlı olarak talep edebilirsiniz.' },
              { Icon: LockIcon, color: '#8b5cf6', title: 'KVKK Onayı', desc: 'Başvuru sırasında KVKK metninizi gösterip onay alabilirsiniz.' },
              { Icon: PhoneAndroidIcon, color: P.main, title: 'SMS Doğrulama (OTP)', desc: 'Telefon numarasını doğrulayarak sahte başvuruları engelleyebilirsiniz.' },
              { Icon: DrawIcon, color: '#10b981', title: 'Dijital İmza', desc: 'Başvuru sırasında imza alınabilir ve belgelerde otomatik kullanılır.' },
              { Icon: NotificationsActiveIcon, color: '#f43f5e', title: 'Mesaj ve Bildirim Yönetimi', desc: 'SMS ve e-posta içeriklerini tamamen özelleştirebilirsiniz.' },
              { Icon: DescriptionIcon, color: '#eab308', title: 'Belge Oluşturma Ayarları', desc: 'Hangi belgelerin otomatik oluşturulacağını siz belirlersiniz.' },
            ].map(({ Icon, color, title, desc }, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box sx={{ p: 3, height: '100%', borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: color, boxShadow: `0 8px 24px ${alpha(color, 0.12)}`, transform: 'translateY(-4px)' } }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Icon sx={{ color }} />
                  </Box>
                  <Typography fontWeight={700} color="#0f172a" mb={1}>{title}</Typography>
                  <Typography variant="body2" color="#64748b" lineHeight={1.7}>{desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4️⃣d KAMERA + BİLDİRİM */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Kamera Entegrasyonu */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff', height: '100%' }}>
                <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: alpha('#f97316', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <PhotoCameraIcon sx={{ color: '#f97316', fontSize: 28 }} />
                </Box>
                <Typography fontWeight={800} color="#0f172a" fontSize="1.25rem" mb={1.5}>
                  Başvuru Sırasında Fotoğraf Alma
                </Typography>
                <Typography variant="body2" color="#475569" lineHeight={1.8} mb={2.5}>
                  {tenantName} ile üyelik başvurularını resimli ve eksiksiz şekilde alabilirsiniz. Kullanıcı tek bir butona tıklayarak telefonunun kamerasını açabilir ve anında kendi fotoğrafını çekebilir.
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Otomatik olarak başvuru formuna eklenir',
                    'Düzenli ve standart şekilde yerleştirilir',
                    'Üyelik kaydı ile birlikte sistemde saklanır',
                    'Mobil uygulamada karekodlu dijital kimlik kartlarında kullanılır',
                    'İstenirse basılı üye kimlik kartı oluştururken otomatik yer alır',
                  ].map((t, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                      <CheckCircleOutlineIcon sx={{ color: '#10b981', fontSize: 18, flexShrink: 0 }} />
                      <Typography variant="body2" color="#334155">{t}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
            {/* Anlık Bildirimler + Esnek Kullanım */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3} height="100%">
                <Box sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: alpha('#f43f5e', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <SmsIcon sx={{ color: '#f43f5e', fontSize: 28 }} />
                  </Box>
                  <Typography fontWeight={800} color="#0f172a" fontSize="1.25rem" mb={1.5}>
                    Anlık Bildirimler
                  </Typography>
                  <Typography variant="body2" color="#475569" lineHeight={1.8}>
                    Yeni bir başvuru geldiğinde yöneticilere anında SMS bildirimi gönderilir. Böylece hiçbir başvuru gözden kaçmaz.
                  </Typography>
                </Box>
                <Box sx={{ p: 4, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff', flexGrow: 1 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: alpha(P.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <DescriptionIcon sx={{ color: P.main, fontSize: 28 }} />
                  </Box>
                  <Typography fontWeight={800} color="#0f172a" fontSize="1.25rem" mb={1.5}>
                    Esnek Belge Yönetimi
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      'PDF olarak indirilebilir',
                      'Başvuru sahibine gönderilebilir',
                      'Dijital olarak arşivlenebilir',
                      '"İndir, İmzala, Yükle" yönlendirmeleri otomatik iletilebilir',
                    ].map((t, i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                        <CheckCircleOutlineIcon sx={{ color: P.main, fontSize: 18, flexShrink: 0 }} />
                        <Typography variant="body2" color="#334155">{t}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#ffffff',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 50%, ${P.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              py: { xs: 6, md: 8 },
              px: { xs: 3, md: 6 },
            }}
          >
            {/* Arka plan deseni */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.15,
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}
            />

            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(50px)',
              }}
            />

            <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                Üye Yönetimini Karmaşadan Kurtarın
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  mb: 5,
                  opacity: 0.95,
                  maxWidth: '580px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                Modüler yapıyla derneğinize uygun şekilde yapılandırın. 
                <br/>
                Üye kayıtlarını tek yerden yönetin.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                 //onClick={() => navigate('/basvuru')}
                  sx={{
                    bgcolor: 'white',
                    color: P.main,
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: '#fafafa',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                 Hemen Başvurun
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/iletisim')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    borderWidth: 2,
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                 İletişime Geçin
                </Button>
              </Stack>

            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

/* 🔹 Küçük bileşenler */

function Feature({ text }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{
        width: 8,
        height: 8,
        bgcolor: P.main,
        borderRadius: '50%'
      }} />
      <Typography fontWeight={600} color="#334155">
        {text}
      </Typography>
    </Stack>
  );
}
